import { InputNumber } from 'antd';
import React, { useCallback } from 'react';

import {
  INTEREST,
  MIN_INTEREST,
} from '@/constants/payment-method/payment.constants';
import { numberWithSpacesToString } from '@/services/string-format.service';

import styles from './price-input.module.scss';

export const PriceInput: React.FC<{
  prefix: string;
  value: number;
  onChange: (value: number, name?: string) => void;   
  onCommit?: (value: number, name?: string) => void;
  name?: string;
  step?: number;
  min?: number;
  max?: number;
  disabled?: boolean;
}> = ({
  prefix,
  value,
  onChange,
  onCommit,
  name,
  step,
  min,
  max,
  disabled,
}) => {
  const effectiveMin =
    min ?? (name === INTEREST ? MIN_INTEREST : 0);

  const clampValue = useCallback(
    (val: number) => {
      if (val < effectiveMin) return effectiveMin;
      if (max !== undefined && val > max) return max;
      return val;
    },
    [effectiveMin, max],
  );

  /* ---------------- LOCAL CHANGE (NO COMMIT) ---------------- */

  const handleChange = (val: number | null) => {
    if (val === null || val === undefined) {
      onChange(effectiveMin, name);
      return;
    }

    onChange(clampValue(val), name);
  };

  /* ---------------- FINAL COMMIT ---------------- */

  const handleCommit = () => {
    if (!onCommit) return;
    onCommit(clampValue(value), name);
  };

  const formatValue = (value: number | undefined) => {
    if (value === undefined) return '';
    return numberWithSpacesToString(value, ' ') || '';
  };

  const parseValue = (displayValue: string | undefined): number => {
    if (!displayValue) return effectiveMin;

    const parsed = Number(displayValue.replace(/\s/g, ''));
    
    if (Number.isNaN(parsed)) return effectiveMin;

    return parsed;
  };

  return (
    <div className={styles.inputWrapper}>
      <span className={styles.inputPrefix}>{prefix}</span>

      <InputNumber
        className="priceInput"
        name={name}
        value={value}
        min={effectiveMin}
        max={max}
        step={step}
        precision={2}
        formatter={formatValue}
        parser={parseValue}
        onChange={handleChange}
        onBlur={handleCommit}          
        onPressEnter={handleCommit}   
        disabled={disabled} 
      />
    </div>
  );
};
